/**
 * Phase F — Discovery Hub
 *
 * Single hub page at /discovery that unifies the discovery surfaces under
 * 5 tabs (Agents | Signals | Scans | Policies | Settings). Each tab's
 * active state lives in the {@code ?tab=} query param so links are
 * bookmark-friendly.
 *
 *   Agents   — embed ShadowDiscoveryPageV2 (zero touch on proven surface)
 *   Signals  — read-only DiscoverySignal chronological list
 *   Scans    — read-only DiscoveryScan list with engine + duration
 *   Policies — Phase E auto-enrollment policy CRUD + dry-run preview
 *   Settings — per-engine DiscoveryRule editor (CODE_REPO GitHub PAT input)
 *
 * Tab content is rendered conditionally; only the active tab mounts its
 * component so the heavy ShadowDiscoveryPageV2 tree isn't paid when the
 * operator is on Policies / Settings.
 *
 * See AGENT_DISCOVERY_DESIGN.md §17 and SYSTEM_WORKFLOW_RULES.md §2.6.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import * as api from '../services/api';
import { TabNav } from '../components/v2/TabNav';
import {
  /* Task #89 — Modal dropped from the import list. CreateRuleModal +
   * CreatePolicyModal both moved to V2 Slideout. ScanTriggerModal is
   * misnamed but internally already uses Slideout. The file is now
   * V2-compliant — no modal anywhere. */
  Button, Pill, Table, EmptyState, Card, Skeleton, Slideout,
} from '../components/v2';
import ShadowDiscoveryPageV2 from './ShadowDiscoveryPageV2';
import { DiscoveryScanProvider, useDiscoveryScanContext } from './discovery/DiscoveryScanContext';
/* DISC-IMPR-B + C — Pre-scan picker modal. Replaces the direct
 * trigger so operators can pick engines and see last-scanned-at
 * before kicking off a redundant full-fan-out scan. */
import ScanTriggerModal from './discovery/ScanTriggerModal';

const TABS = [
  { id: 'agents',   label: 'Agents',   description: 'Discovered shadow + governed inventory' },
  { id: 'signals',  label: 'Signals',  description: 'Per-engine detection signals' },
  { id: 'scans',    label: 'Scans',    description: 'Recent discovery scan runs' },
  { id: 'policies', label: 'Policies', description: 'Auto-enrollment rule expressions' },
  { id: 'settings', label: 'Settings', description: 'Engine credentials + scan rules' },
];

export default function DiscoveryHubPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeId = TABS.find((t) => t.id === requestedTab) ? requestedTab : 'agents';

  const onChange = useCallback((id) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  // Phase H — used by the drawer to nudge the Scans/Agents tabs to
  // re-fetch once a run completes. Cheap version: bump a generation
  // counter that child tabs include in their useEffect deps. We could
  // wire this more granularly later if it becomes a perf concern; for
  // now Discovery has at most a few dozen rows per tab.
  const [refreshGeneration, setRefreshGeneration] = useState(0);
  const handleScanCompleted = useCallback(() => {
    setRefreshGeneration((g) => g + 1);
  }, []);

  return (
    <DiscoveryScanProvider onScanCompleted={handleScanCompleted}>
      <div className="min-h-full bg-v2-page text-v2-ink">
        <header className="px-7 pt-7 pb-3 max-w-[1600px] mx-auto">
          <h1 className="text-[22px] font-bold tracking-tight m-0">Discovery</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            Shadow + governed agents, scan runs, signals, auto-enrollment policies, and engine configuration.
          </p>
        </header>
        <div className="px-7 max-w-[1600px] mx-auto">
          <TabNav
            tabs={TABS.map((t) => ({ id: t.id, label: t.label }))}
            activeId={activeId}
            onChange={onChange}
            ariaLabel="Discovery hub sections"
          />
        </div>
        <div className="max-w-[1600px] mx-auto">
          {activeId === 'agents'   && <AgentsTabContent />}
          {activeId === 'signals'  && <SignalsTab key={`signals-${refreshGeneration}`} />}
          {activeId === 'scans'    && <ScansTab key={`scans-${refreshGeneration}`} />}
          {activeId === 'policies' && <PoliciesTab />}
          {activeId === 'settings' && <SettingsTab />}
        </div>
      </div>
    </DiscoveryScanProvider>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Tab 1 — Agents (reuse existing surface, zero-touch)
// ══════════════════════════════════════════════════════════════════════

function AgentsTabContent() {
  // ShadowDiscoveryPageV2 owns its own header + counters + facets + table.
  // We render it bare so the hub's title + tab nav stay above it.
  return <ShadowDiscoveryPageV2 />;
}

// ══════════════════════════════════════════════════════════════════════
// Tab 2 — Signals (read-only chronological list)
// ══════════════════════════════════════════════════════════════════════

function SignalsTab() {
  const [signals, setSignals] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();
  /* DEF-DISC-SIGNAL-STATUS-001 — Per-row pending state so the operator
   * can see WHICH row is being resolved during the round-trip. Tracks
   * signal.id → boolean. */
  const [resolvingIds, setResolvingIds] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.shadowDiscoverySignals();
      const arr = Array.isArray(r) ? r : (r?.data?.content || r?.data || r?.content || []);
      setSignals(Array.isArray(arr) ? arr : []);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Failed to load signals', description: e?.message || String(e) });
      setSignals([]);
    } finally { setLoading(false); }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  /* DEF-DISC-SIGNAL-STATUS-001 — Click handler for the Open pill. Hits
   * the shadowDiscoveryResolveSignal endpoint that already existed but
   * was never wired from the UI. Optimistically marks the row resolved
   * locally so the operator sees immediate feedback; reverts on backend
   * failure with a toast that surfaces the real error. */
  const handleResolve = useCallback(async (signal) => {
    if (!signal?.id || signal.resolved) return;
    setResolvingIds((m) => ({ ...m, [signal.id]: true }));
    try {
      const res = await api.shadowDiscoveryResolveSignal(signal.id);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Resolve failed',
          description: res.error || res.data?.error || `HTTP ${res.status || '?'}` });
        return;
      }
      addToast({ tone: 'success', title: 'Signal resolved',
        description: `${signal.signalType || 'Signal'} marked resolved.` });
      /* Optimistic local update — flip the row's resolved flag rather
       * than re-fetching the whole list. A background reload picks up
       * any peer-edited rows the next time the operator hits Refresh. */
      setSignals((prev) => Array.isArray(prev)
        ? prev.map((s) => s.id === signal.id ? { ...s, resolved: true } : s)
        : prev);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Resolve failed', description: e?.message || String(e) });
    } finally {
      setResolvingIds((m) => {
        const next = { ...m };
        delete next[signal.id];
        return next;
      });
    }
  }, [addToast]);

  const severityTone = (s) => ({
    CRITICAL: 'danger', HIGH: 'danger', MEDIUM: 'warning', LOW: 'cyan', INFO: 'muted',
  }[s] || 'muted');

  return (
    <div className="p-7">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[16px] font-semibold m-0">Signals</h2>
          <p className="text-[12px] text-v2-ink-secondary m-0">
            {signals == null ? 'Loading…' : `${signals.length} signal(s) across all engines`}
          </p>
        </div>
        <Button variant="ghost" onClick={load} loading={loading}>Refresh</Button>
      </div>
      {signals == null && <Card><Card.Body><Skeleton variant="rect" height={200} /></Card.Body></Card>}
      {signals != null && signals.length === 0 && (
        <EmptyState
          title="No signals yet"
          description="Signals appear as engines detect agentic behavior, code, or protocols. Trigger a scan from the Agents tab or wait for the next scheduled scan."
        />
      )}
      {signals != null && signals.length > 0 && (
        <Table
          ariaLabel="Discovery signals"
          columns={[
            { key: 'engine', header: 'Engine', render: (r) => (
              <Pill tone="muted" size="sm">{r.engineType || '—'}</Pill>
            )},
            { key: 'signalType', header: 'Signal', render: (r) => (
              <span className="font-mono-v2 text-[11px]">{r.signalType || '—'}</span>
            )},
            { key: 'severity', header: 'Severity', render: (r) => (
              <Pill tone={severityTone(r.severity)} size="sm" dot>{r.severity || '—'}</Pill>
            )},
            { key: 'confidence', header: 'Confidence', render: (r) => `${r.confidence ?? 0}%` },
            { key: 'detected', header: 'Detected', render: (r) =>
              r.detectedAt ? new Date(r.detectedAt).toLocaleString() : '—' },
            /* DEF-DISC-SIGNAL-STATUS-001 — Open signals are now
             * actionable. Click to resolve via shadowDiscoveryResolveSignal.
             * Resolved rows stay as static pills since the resolve
             * endpoint is one-way (no re-open today). Pending state
             * disables the click + shows "Resolving…". */
            { key: 'resolved', header: 'Status', render: (r) => {
              if (r.resolved) {
                return <Pill tone="success" size="sm">Resolved</Pill>;
              }
              const pending = !!resolvingIds[r.id];
              return (
                <button
                  type="button"
                  onClick={() => handleResolve(r)}
                  disabled={pending}
                  className={
                    'v2-focus-ring inline-flex items-center rounded-v2-pill text-[10.5px] font-semibold ' +
                    'uppercase tracking-wider px-2 py-0.5 ' +
                    (pending
                      ? 'bg-v2-muted text-v2-ink-muted cursor-wait'
                      : 'bg-v2-warning-soft text-v2-warning hover:bg-v2-warning hover:text-white transition-colors')
                  }
                  title={pending ? 'Resolving…' : 'Click to mark this signal resolved'}
                >
                  {pending ? 'Resolving…' : 'Open · resolve'}
                </button>
              );
            }},
          ]}
          rows={signals}
          rowKey={(r) => r.id}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Tab 3 — Scans (read-only list of recent runs)
// ══════════════════════════════════════════════════════════════════════

function ScansTab() {
  const [scans, setScans] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();
  /* DEF-DISC-SCAN-DRILLDOWN-001 — Per-row drill-down. Each scan row in
   * the table is one (run × engine) tuple after the BE fan-out, so
   * clicking opens a slideout that fetches the FULL run (all engines +
   * totals) via shadowDiscoveryScanRun and renders the breakdown. */
  const [selectedRunId, setSelectedRunId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.shadowDiscoveryScans();
      const arr = Array.isArray(r) ? r : (r?.data?.content || r?.data || r?.content || []);
      setScans(Array.isArray(arr) ? arr : []);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Failed to load scans', description: e?.message || String(e) });
      setScans([]);
    } finally { setLoading(false); }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const statusTone = (s) => ({
    COMPLETED: 'success', RUNNING: 'cyan', FAILED: 'danger', PENDING: 'muted',
  }[s] || 'muted');

  const durationMs = (started, finished) => {
    if (!started || !finished) return null;
    return Math.max(0, new Date(finished).getTime() - new Date(started).getTime());
  };
  const fmtDuration = (ms) => {
    if (ms == null) return '—';
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
    return `${(ms / 60_000).toFixed(1)} m`;
  };

  return (
    <div className="p-7">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[16px] font-semibold m-0">Scans</h2>
          <p className="text-[12px] text-v2-ink-secondary m-0">
            {scans == null ? 'Loading…' : `${scans.length} recent scan(s)`}
          </p>
        </div>
        <ScansTabActions onRefresh={load} loading={loading} />
      </div>
      {scans == null && <Card><Card.Body><Skeleton variant="rect" height={200} /></Card.Body></Card>}
      {scans != null && scans.length === 0 && (
        <EmptyState
          title="No scans recorded yet"
          description="Discovery scans appear here once the scheduler fires the first run, or you trigger a manual scan from the Agents tab."
        />
      )}
      {scans != null && scans.length > 0 && (
        <Table
          ariaLabel="Discovery scans"
          columns={[
            { key: 'engine', header: 'Engine', render: (r) => (
              <Pill tone="muted" size="sm">{r.engineType || '—'}</Pill>
            )},
            { key: 'status', header: 'Status', render: (r) => (
              <Pill tone={statusTone(r.status)} size="sm" dot>{r.status || '—'}</Pill>
            )},
            { key: 'started', header: 'Started', render: (r) =>
              r.startedAt ? new Date(r.startedAt).toLocaleString() : '—' },
            { key: 'duration', header: 'Duration', render: (r) =>
              fmtDuration(durationMs(r.startedAt, r.completedAt)) },
            { key: 'agents', header: 'Agents', render: (r) => r.agentsDiscovered ?? 0 },
            { key: 'signals', header: 'Signals', render: (r) => r.signalsGenerated ?? 0 },
            { key: 'triggered', header: 'Triggered by', render: (r) =>
              r.scanType === 'ON_DEMAND' ? 'Manual' : (r.scanType === 'SCHEDULED' ? 'Schedule' : (r.scanType || '—')) },
          ]}
          rows={scans}
          rowKey={(r) => r.id}
          /* DEF-DISC-SCAN-DRILLDOWN-001 — Row click opens the run-level
           * drill-down slideout. The row's `runId` (set in the BE
           * fan-out) is the lookup key for /scans/runs/{runId}. Rows
           * without a runId (legacy shape) silently no-op so we don't
           * crash on old data. */
          onRowClick={(r) => { if (r?.runId) setSelectedRunId(r.runId); }}
        />
      )}
      <ScanDetailSlideout
        runId={selectedRunId}
        onClose={() => setSelectedRunId(null)}
      />
    </div>
  );
}

/**
 * DEF-DISC-SCAN-DRILLDOWN-001 — Slideout that fetches the full
 * DiscoveryScanRun (all 6 engines + run-level totals) and renders the
 * breakdown so the operator can see what each engine found, which
 * targets were scanned, and whether any agents were auto-promoted by
 * policy. Uses shadowDiscoveryScanRun(runId) — the same endpoint the
 * live progress drawer polls — so the surface is consistent.
 *
 * <p>Renders nothing when {@code runId} is null. The fetch fires fresh
 * each time the operator selects a new run.
 */
function ScanDetailSlideout({ runId, onClose }) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!runId) { setRun(null); setError(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.shadowDiscoveryScanRun(runId)
      .then((res) => {
        if (cancelled) return;
        if (res && res.ok === false) {
          /* DEF-DISC-SCAN-MANUAL-404-001 — 404 here means the full run
           * record has been evicted (tracker retention) or never
           * persisted (legacy pre-V224 scan). Don't surface a red
           * "Failed to load: HTTP 404" — the FE list still has the
           * per-engine row data the operator was looking at; show a
           * friendly empty state instead and the row's summary data
           * stays visible behind it. */
          const is404 = res.status === 404 || /404|not found/i.test(res.error || '');
          if (is404) {
            setRun({ __notFound: true, runId });
            setError(null);
            return;
          }
          setError(res.error || `HTTP ${res.status || '?'}`);
          setRun(null);
          return;
        }
        setRun(res?.data || res || null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || String(e));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [runId]);

  const isOpen = !!runId;
  const engines = run?.engines ? Object.entries(run.engines) : [];

  const phaseTone = (p) => ({
    COMPLETED: 'success', FAILED: 'danger',
    RUNNING: 'cyan', SCANNING: 'cyan', CORRELATING: 'cyan', FINALIZING: 'cyan',
    QUEUED: 'muted',
  }[p] || 'muted');

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={620}
      ariaLabel="Scan run detail"
      header={
        <div>
          <h2 className="text-[15px] font-bold m-0">Discovery scan run</h2>
          {run && (
            <div className="flex items-center gap-2 mt-1 text-[11.5px]">
              <Pill tone={phaseTone(run.phase)} size="sm" dot>{run.phase || '—'}</Pill>
              <span className="text-v2-ink-muted font-mono-v2">
                {run.runId ? String(run.runId).slice(0, 8) + '…' : ''}
              </span>
            </div>
          )}
        </div>
      }
    >
      <div className="space-y-4 text-[12.5px]">
        {loading && <Skeleton variant="rect" height={180} />}
        {error && (
          <div className="rounded-v2-sm border border-v2-danger bg-v2-danger-soft text-v2-danger px-3 py-2">
            <strong>Failed to load run:</strong> {error}
          </div>
        )}
        {run?.__notFound && !loading && (
          <div className="rounded-v2-md border border-v2-border bg-v2-muted px-4 py-3 text-v2-ink-secondary">
            <p className="text-[13px] font-semibold text-v2-ink m-0 mb-1">Run detail no longer available</p>
            <p className="text-[12px] leading-relaxed m-0">
              This scan run completed too long ago for full drill-down — the per-engine state has been evicted
              from the live tracker. The row summary visible behind this drawer still reflects what we recorded
              at scan time. To get a fresh drill-down, click <strong>Run scan</strong> on the Agents tab to start a new run.
            </p>
            <p className="text-[10.5px] text-v2-ink-muted mt-2 font-mono-v2">runId · {String(run.runId).slice(0, 8)}…</p>
          </div>
        )}
        {run && !run.__notFound && !loading && (
          <>
            {/* Run-level summary */}
            <Card padding="sm">
              <Card.Header title="Summary" />
              <Card.Body>
                <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-center">
                  <div>
                    <dd className="text-[20px] font-bold tabular-nums leading-tight">
                      {run.totalAgentsDiscovered ?? 0}
                    </dd>
                    <dt className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted mt-1">
                      Agents discovered
                    </dt>
                  </div>
                  <div>
                    <dd className="text-[20px] font-bold tabular-nums leading-tight">
                      {run.totalSignalsGenerated ?? 0}
                    </dd>
                    <dt className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted mt-1">
                      Signals
                    </dt>
                  </div>
                  <div>
                    <dd className="text-[20px] font-bold tabular-nums leading-tight">
                      {run.autoPromotedByPolicy ?? 0}
                    </dd>
                    <dt className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted mt-1">
                      Auto-promoted
                    </dt>
                  </div>
                </dl>
                <div className="grid grid-cols-2 gap-3 mt-4 text-[11.5px]">
                  <div>
                    <div className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">
                      Started
                    </div>
                    <div className="font-mono-v2">
                      {run.startedAt ? new Date(run.startedAt).toLocaleString() : '—'}
                    </div>
                  </div>
                  <div>
                    <div className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">
                      Completed
                    </div>
                    <div className="font-mono-v2">
                      {run.completedAt ? new Date(run.completedAt).toLocaleString() : '—'}
                    </div>
                  </div>
                </div>
                {run.correlatedSignalCount > 0 && (
                  <div className="mt-3 text-[11px] text-v2-ink-muted">
                    Correlated {run.correlatedSignalCount} signal(s) across engines.
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Per-engine breakdown */}
            <Card padding="sm">
              <Card.Header title={`Engines · ${engines.length}`} />
              <Card.Body>
                {engines.length === 0 ? (
                  <p className="text-v2-ink-muted">No engine state recorded.</p>
                ) : (
                  <ul className="space-y-3">
                    {engines.map(([engineType, es]) => {
                      const elapsed = es?.elapsedMs;
                      const targets = Array.isArray(es?.targets) ? es.targets : [];
                      const notes = Array.isArray(es?.notes) ? es.notes : [];
                      return (
                        <li key={engineType} className="border-l-2 border-v2-border-subtle pl-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-v2-ink">{engineType}</span>
                            <Pill tone={phaseTone(es?.state)} size="sm" dot>{es?.state || 'UNKNOWN'}</Pill>
                            {typeof elapsed === 'number' && (
                              <span className="text-[10.5px] text-v2-ink-muted font-mono-v2">
                                {elapsed < 1000 ? `${elapsed}ms` : `${(elapsed / 1000).toFixed(1)}s`}
                              </span>
                            )}
                            <span className="ml-auto text-[11px] text-v2-ink-muted">
                              {es?.agentsDiscovered ?? 0} agents · {es?.signalsGenerated ?? 0} signals
                            </span>
                          </div>
                          {targets.length > 0 && (
                            <div className="mt-1">
                              <div className="text-[10px] uppercase tracking-wider text-v2-ink-muted">
                                Targets · {targets.length}
                              </div>
                              <ul className="space-y-0.5 mt-0.5">
                                {targets.slice(0, 6).map((t, i) => (
                                  <li key={i} className="text-[11px] text-v2-ink-secondary font-mono-v2 truncate" title={t}>
                                    · {t}
                                  </li>
                                ))}
                                {targets.length > 6 && (
                                  <li className="text-[10.5px] text-v2-ink-muted italic">
                                    …and {targets.length - 6} more
                                  </li>
                                )}
                              </ul>
                            </div>
                          )}
                          {es?.errorMessage && (
                            <div className="mt-1 text-[11px] text-v2-danger font-mono-v2 break-words">
                              {es.errorMessage}
                            </div>
                          )}
                          {notes.length > 0 && (
                            <ul className="mt-1 space-y-0.5">
                              {notes.slice(-3).map((n, i) => (
                                <li key={i} className="text-[11px] text-v2-ink-secondary">↳ {n}</li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card.Body>
            </Card>

            {run.fatalErrorMessage && (
              <Card padding="sm">
                <Card.Header title="Fatal error" />
                <Card.Body>
                  <div className="text-v2-danger font-mono-v2 break-words">
                    {run.fatalErrorMessage}
                  </div>
                </Card.Body>
              </Card>
            )}

            {Array.isArray(run.notes) && run.notes.length > 0 && (
              <Card padding="sm">
                <Card.Header title="Run notes" />
                <Card.Body>
                  <ul className="space-y-1">
                    {run.notes.map((n, i) => (
                      <li key={i} className="text-[11.5px] text-v2-ink-secondary">• {n}</li>
                    ))}
                  </ul>
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
    </Slideout>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Tab 4 — Policies (Phase E auto-enrollment CRUD + dry-run)
// ══════════════════════════════════════════════════════════════════════

const POLICY_PRESETS = [
  { label: 'Auto-enroll high-confidence MCP servers',
    description: 'agentType=MCP_SERVER AND confidence>=80 AND riskTier IN (LOW,MEDIUM)',
    expression: { and: [
      { '==': [{ var: 'agentType' }, 'MCP_SERVER'] },
      { '>=': [{ var: 'confidence' }, 80] },
      { in: [{ var: 'riskTier' }, ['LOW', 'MEDIUM']] },
    ]},
  },
  { label: 'Auto-enroll low-risk SDK detections',
    description: 'sourceEngine=CODE_REPO AND riskTier=LOW AND confidence>=70',
    expression: { and: [
      { '==': [{ var: 'sourceEngine' }, 'CODE_REPO'] },
      { '==': [{ var: 'riskTier' }, 'LOW'] },
      { '>=': [{ var: 'confidence' }, 70] },
    ]},
  },
  { label: 'Auto-enroll any HIGH-confidence non-CRITICAL discovery',
    description: 'confidence>=90 AND riskTier!=CRITICAL',
    expression: { and: [
      { '>=': [{ var: 'confidence' }, 90] },
      { '!=': [{ var: 'riskTier' }, 'CRITICAL'] },
    ]},
  },
];

/**
 * Phase H — scan-tab action row. Lives next to "Refresh" and exposes a
 * "Run scan" button that fires the async trigger via the
 * DiscoveryScanProvider, so the live drawer takes over with engine-by-
 * engine progress. We render the button inline (not as a top-level
 * hub action) so the operator's mental model stays consistent with the
 * Scans tab being the home for scan runs.
 */
function ScansTabActions({ onRefresh, loading }) {
  const scanCtx = useDiscoveryScanContext();
  const { addToast } = useToast();
  /* DISC-IMPR-B/C — Modal-driven trigger replaces the direct
   * "Run scan" click. The modal fetches the catalog, default-deselects
   * engines scanned in the last 6 hours, and confirms with an explicit
   * payload so the BE runs exactly the subset the operator chose.
   *
   * Phase 2 (#93) — payload shape is now
   * {enginePicks:[{engineType, ruleIds, targets}, …]}. The legacy
   * engineTypes-array shape is still accepted for back-compat with any
   * test/dev caller that hasn't migrated yet. */
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleConfirm = async (payload) => {
    if (!scanCtx) {
      addToast({ tone: 'warning', title: 'Scan trigger unavailable', description: 'Live progress provider not mounted.' });
      return;
    }
    /* Normalise either shape into the kwargs DiscoveryScanContext expects. */
    let triggerArgs;
    let summaryLabel;
    if (payload && Array.isArray(payload.enginePicks)) {
      triggerArgs = { enginePicks: payload.enginePicks };
      const engineCount = payload.enginePicks.length;
      const ruleCount = payload.enginePicks.reduce((sum, p) => sum + ((p.ruleIds || []).length), 0);
      const tgtCount = payload.enginePicks.reduce((sum, p) => sum + ((p.targets || []).length), 0);
      const ruleSeg = ruleCount > 0 ? `${ruleCount} rule${ruleCount === 1 ? '' : 's'}` : 'all enabled rules';
      const tgtSeg = tgtCount > 0 ? ` with ${tgtCount} target override${tgtCount === 1 ? '' : 's'}` : '';
      summaryLabel = `${engineCount} engine${engineCount === 1 ? '' : 's'} · ${ruleSeg}${tgtSeg}`;
    } else if (Array.isArray(payload)) {
      // legacy engineTypes-array shape
      triggerArgs = { engineTypes: payload };
      summaryLabel = payload.length > 0
        ? `${payload.length} engine${payload.length === 1 ? '' : 's'}`
        : 'all engines';
    } else {
      triggerArgs = {};
      summaryLabel = 'all engines';
    }
    try {
      await scanCtx.triggerScan(triggerArgs);
      setPickerOpen(false);
      addToast({
        tone: 'info',
        title: 'Discovery scan started',
        description: `Running ${summaryLabel}. See the progress drawer for live engine status.`,
      });
    } catch (err) {
      addToast({ tone: 'danger', title: 'Scan failed', description: err?.message || String(err) });
    }
  };
  return (
    <>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onRefresh} loading={loading}>Refresh</Button>
        {/* scanCtx.running, not scanCtx.runId: runId stays set while the drawer sits
            collapsed as a completion pill, which left this button disabled and
            reading "Scan running…" next to a pill saying the scan had finished. */}
        <Button
          variant="primary"
          onClick={() => setPickerOpen(true)}
          disabled={!!scanCtx?.running || scanCtx?.triggering}
          loading={scanCtx?.triggering}
        >
          {scanCtx?.running ? 'Scan running…' : 'Run scan'}
        </Button>
      </div>
      <ScanTriggerModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={handleConfirm}
        triggering={!!scanCtx?.triggering}
      />
    </>
  );
}

function PoliciesTab() {
  const [policies, setPolicies] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const { addToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listAutoEnrollmentPolicies();
      const arr = Array.isArray(r) ? r : (r?.data || []);
      setPolicies(Array.isArray(arr) ? arr : []);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Failed to load policies', description: e?.message || String(e) });
      setPolicies([]);
    } finally { setLoading(false); }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete auto-enrollment policy "${name}"?\n\nFuture scans won't auto-promote based on this rule. Existing already-enrolled agents stay enrolled.`)) return;
    try {
      await api.deleteAutoEnrollmentPolicy(id);
      addToast({ tone: 'success', title: 'Policy deleted' });
      load();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Delete failed', description: e?.message });
    }
  };

  const handleToggle = async (policy) => {
    try {
      await api.updateAutoEnrollmentPolicy(policy.id, { isActive: !policy.isActive });
      addToast({ tone: 'success', title: policy.isActive ? 'Policy deactivated' : 'Policy activated' });
      load();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Toggle failed', description: e?.message });
    }
  };

  return (
    <div className="p-7">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[16px] font-semibold m-0">Auto-enrollment policies</h2>
          <p className="text-[12px] text-v2-ink-secondary m-0">
            {policies == null ? 'Loading…'
              : `${policies.length} policy(ies) · ${policies.filter((p) => p.isActive).length} active`}
          </p>
          <p className="text-[11px] text-v2-ink-muted mt-1 max-w-[700px]">
            JSONLogic rules evaluated against every NEW DiscoveredAgent after each scan completes.
            Matches auto-promote to ENROLLED with method=AUTO_POLICY. See SYSTEM_WORKFLOW_RULES.md §2.6.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={load} loading={loading}>Refresh</Button>
          <Button variant="primary" onClick={() => setShowCreate(true)}>+ New policy</Button>
        </div>
      </div>

      {policies == null && <Card><Card.Body><Skeleton variant="rect" height={200} /></Card.Body></Card>}
      {policies != null && policies.length === 0 && (
        <EmptyState
          title="No policies yet"
          description="Create your first auto-enrollment policy to stop manually triaging every discovery. Pick a preset or write your own JSONLogic expression."
          primaryAction={<Button variant="primary" onClick={() => setShowCreate(true)}>+ New policy</Button>}
        />
      )}
      {policies != null && policies.length > 0 && (
        <Table
          ariaLabel="Auto-enrollment policies"
          columns={[
            { key: 'name', header: 'Name', render: (p) => (
              <div>
                <div className="font-semibold">{p.name}</div>
                <div className="text-[11px] text-v2-ink-muted">{p.description || '—'}</div>
              </div>
            )},
            { key: 'expression', header: 'Expression', render: (p) => (
              <code className="text-[10.5px] font-mono-v2 text-v2-ink-secondary bg-v2-muted px-2 py-1 rounded inline-block max-w-[420px] truncate">
                {JSON.stringify(p.expression)}
              </code>
            )},
            { key: 'status', header: 'Status', render: (p) => (
              <Pill tone={p.isActive ? 'success' : 'muted'} dot size="sm">
                {p.isActive ? 'Active' : 'Inactive'}
              </Pill>
            )},
            { key: 'updated', header: 'Updated', render: (p) =>
              p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '—' },
            { key: 'actions', header: '', align: 'right', render: (p) => (
              <div className="flex items-center gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => handleToggle(p)}>
                  {p.isActive ? 'Pause' : 'Activate'}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id, p.name)}>
                  Delete
                </Button>
              </div>
            )},
          ]}
          rows={policies}
          rowKey={(p) => p.id}
        />
      )}

      {showCreate && (
        <CreatePolicyModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

function CreatePolicyModal({ onClose, onCreated }) {
  const { addToast } = useToast();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [exprText, setExprText] = useState(JSON.stringify(POLICY_PRESETS[0].expression, null, 2));
  const [isActive, setIsActive] = useState(true);
  const [dryRunResults, setDryRunResults] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exprError, setExprError] = useState(null);

  const applyPreset = (preset) => {
    setName(preset.label);
    setDescription(preset.description);
    setExprText(JSON.stringify(preset.expression, null, 2));
    setExprError(null);
    setDryRunResults(null);
  };

  const parseExpr = () => {
    try {
      const parsed = JSON.parse(exprText);
      setExprError(null);
      return parsed;
    } catch (e) {
      setExprError(e.message);
      return null;
    }
  };

  const handleDryRun = async () => {
    const expr = parseExpr();
    if (!expr) return;
    setDryRunLoading(true);
    try {
      const r = await api.dryRunAutoEnrollmentPolicy(expr, 50);
      setDryRunResults(r?.data || r);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Dry-run failed', description: e?.message });
    } finally { setDryRunLoading(false); }
  };

  const handleSave = async () => {
    if (!name.trim()) { addToast({ tone: 'warning', title: 'Name required' }); return; }
    const expr = parseExpr();
    if (!expr) { addToast({ tone: 'warning', title: 'Expression is not valid JSON' }); return; }
    setSaving(true);
    try {
      await api.createAutoEnrollmentPolicy({ name: name.trim(), description: description.trim(), expression: expr, isActive });
      addToast({ tone: 'success', title: `Policy "${name}" created` });
      onCreated();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Create failed', description: e?.message });
    } finally { setSaving(false); }
  };

  return (
    /* Task #89 — Auto-enrollment policy form converted from V1 Modal to
     * V2 Slideout. Same V2 redesign mandate as the New Rule form
     * directly above. Width 520 accommodates the JSONLogic textarea +
     * dry-run results panel without horizontal cramping. */
    <Slideout
      isOpen={true}
      onClose={onClose}
      ariaLabel="New auto-enrollment policy"
      width={520}
      header={
        <div>
          <h2 className="text-[15px] font-bold tracking-tight m-0">New auto-enrollment policy</h2>
          <p className="text-[12px] text-v2-ink-secondary m-0 mt-0.5 leading-relaxed">
            JSONLogic rule evaluated against every new DiscoveredAgent. Matches auto-promote to ENROLLED.
          </p>
        </div>
      }
    >
      <div className="space-y-4 text-[12.5px]">
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
            Presets
          </label>
          <div className="flex flex-wrap gap-2">
            {POLICY_PRESETS.map((p) => (
              <Button key={p.label} variant="ghost" size="sm" onClick={() => applyPreset(p)}>
                {p.label}
              </Button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
            Name
          </label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full bg-v2-surface border border-v2-border rounded-v2-sm px-3 py-2 text-[12.5px] outline-none focus:border-v2-action"
            placeholder="e.g. Auto-enroll low-risk SDK detections" />
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
            Description
          </label>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
            className="w-full bg-v2-surface border border-v2-border rounded-v2-sm px-3 py-2 text-[12.5px] outline-none focus:border-v2-action"
            placeholder="Free-text rationale" />
        </div>

        <div>
          <div className="flex items-baseline justify-between mb-1">
            <label className="text-[10px] uppercase tracking-wider font-semibold text-v2-ink-muted">
              Expression (JSONLogic)
            </label>
            <a href="https://jsonlogic.com/operations.html" target="_blank" rel="noopener noreferrer"
               className="text-[10.5px] text-v2-action hover:underline">
              JSONLogic operators →
            </a>
          </div>
          <textarea value={exprText} onChange={(e) => { setExprText(e.target.value); setExprError(null); }}
            className="w-full bg-v2-muted border border-v2-border rounded-v2-sm px-3 py-2 text-[11.5px] font-mono-v2 outline-none focus:border-v2-action"
            rows={10} spellCheck="false" />
          {exprError && (
            <div className="text-[11px] text-v2-danger mt-1">JSON parse error: {exprError}</div>
          )}
          <div className="text-[10.5px] text-v2-ink-muted mt-1">
            Variables: <code>confidence</code> (0-100), <code>riskTier</code> (CRITICAL/HIGH/MEDIUM/LOW),
            <code> agentType</code>, <code>sourceEngine</code> (CODE_REPO/NETWORK/...), <code>platformType</code>,
            <code> status</code>, <code>name</code>, <code>networkIdentifier</code>.
          </div>
        </div>

        <label className="flex items-center gap-2 text-[12.5px]">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Activate immediately on save
        </label>

        <div className="border-t border-v2-border-subtle pt-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-[13px] font-semibold m-0">Dry-run preview</h3>
            <Button variant="secondary" size="sm" onClick={handleDryRun} loading={dryRunLoading}>
              Test against current discoveries
            </Button>
          </div>
          {dryRunResults && (
            <div className="bg-v2-muted rounded-v2-sm p-3 text-[11.5px]">
              <div className="font-semibold mb-2">
                {dryRunResults.matchCount ?? 0} match(es) across {dryRunResults.totalScanned ?? 0} candidate(s)
              </div>
              <div className="max-h-[180px] overflow-y-auto">
                {(dryRunResults.results || []).slice(0, 20).map((r) => (
                  <div key={r.discoveredAgentId} className="flex items-center gap-2 py-1 border-b border-v2-border-subtle last:border-b-0">
                    <Pill tone={r.matched ? 'success' : 'muted'} size="sm">
                      {r.matched ? '✓ match' : 'no'}
                    </Pill>
                    <span className="font-mono-v2 text-[10.5px]">{r.sourceEngine}</span>
                    <span className="text-v2-ink-secondary">conf={r.confidence ?? 0}</span>
                    <span className="text-v2-ink-secondary">risk={r.riskTier}</span>
                    <span className="truncate">{r.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-v2-border-subtle">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} loading={saving}>Save policy</Button>
        </div>
      </div>
    </Slideout>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Tab 5 — Settings (DiscoveryRule editor, CODE_REPO focused)
// ══════════════════════════════════════════════════════════════════════

function SettingsTab() {
  const [rules, setRules] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { addToast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.shadowDiscoveryRules();
      const arr = Array.isArray(r) ? r : (r?.data || r?.content || []);
      setRules(Array.isArray(arr) ? arr : []);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Failed to load rules', description: e?.message || String(e) });
      setRules([]);
    } finally { setLoading(false); }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (rule) => {
    try {
      await api.shadowDiscoveryToggleRule(rule.id, { enabled: !rule.enabled });
      addToast({ tone: 'success', title: rule.enabled ? 'Rule disabled' : 'Rule enabled' });
      load();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Toggle failed', description: e?.message });
    }
  };

  const handleDelete = async (rule) => {
    setDeleting(true);
    try {
      const res = await api.shadowDiscoveryDeleteRule(rule.id);
      if (res && res.ok === false) throw new Error(res.error || 'delete failed');
      addToast({ tone: 'success', title: 'Rule deleted' });
      setConfirmDeleteId(null);
      load();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Delete failed', description: e?.message });
    } finally { setDeleting(false); }
  };

  return (
    <div className="p-7">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[16px] font-semibold m-0">Engine rules</h2>
          <p className="text-[12px] text-v2-ink-secondary m-0">
            {rules == null ? 'Loading…'
              : `${rules.length} rule(s) · ${rules.filter((r) => r.enabled).length} enabled`}
          </p>
          <p className="text-[11px] text-v2-ink-muted mt-1 max-w-[700px]">
            Per-engine configuration. CODE_REPO requires a GitHub org + Personal Access Token. Other engines
            (NETWORK, MCP, CLOUD, IDENTITY, BEHAVIORAL) use rule-driven detection without external credentials.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={load} loading={loading}>Refresh</Button>
          <Button variant="primary" onClick={() => setShowCreate(true)}>+ New rule</Button>
        </div>
      </div>

      {rules == null && <Card><Card.Body><Skeleton variant="rect" height={200} /></Card.Body></Card>}
      {rules != null && rules.length === 0 && (
        <EmptyState
          title="No discovery rules configured yet"
          description="Add a rule to enable scanning. The most common first step: a CODE_REPO rule with your GitHub org + PAT so the next scheduled scan walks your repos for agentic frameworks."
          primaryAction={<Button variant="primary" onClick={() => setShowCreate(true)}>+ Configure CODE_REPO</Button>}
        />
      )}
      {rules != null && rules.length > 0 && (
        <Table
          ariaLabel="Discovery rules"
          columns={[
            { key: 'engine', header: 'Engine', render: (r) => (
              <Pill tone="muted" size="sm">{r.engineType || '—'}</Pill>
            )},
            { key: 'name', header: 'Rule name', render: (r) => r.ruleName || '—' },
            { key: 'enabled', header: 'Status', render: (r) => (
              <Pill tone={r.enabled ? 'success' : 'muted'} dot size="sm">
                {r.enabled ? 'Enabled' : 'Disabled'}
              </Pill>
            )},
            { key: 'preview', header: 'Config preview', render: (r) => (
              <code className="text-[10.5px] font-mono-v2 text-v2-ink-secondary bg-v2-muted px-2 py-1 rounded inline-block max-w-[380px] truncate">
                {sanitiseConfigPreview(r.ruleConfig)}
              </code>
            )},
            { key: 'actions', header: '', align: 'right', render: (r) => {
              if (confirmDeleteId === r.id) {
                return (
                  <div className="flex items-center gap-1.5 justify-end">
                    <span className="text-[11px] text-v2-ink-muted">Delete this rule?</span>
                    <Button size="xs" variant="danger" loading={deleting} onClick={() => handleDelete(r)}>Yes</Button>
                    <Button size="xs" variant="ghost" disabled={deleting} onClick={() => setConfirmDeleteId(null)}>No</Button>
                  </div>
                );
              }
              return (
                <div className="flex items-center gap-1.5 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setEditRule(r)}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleToggle(r)}>
                    {r.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteId(r.id)}>Delete</Button>
                </div>
              );
            }},
          ]}
          rows={rules}
          rowKey={(r) => r.id}
        />
      )}

      {(showCreate || editRule) && (
        <CreateRuleModal
          editRule={editRule}
          onClose={() => { setShowCreate(false); setEditRule(null); }}
          onCreated={() => { setShowCreate(false); setEditRule(null); load(); }}
        />
      )}
    </div>
  );
}

/** Redact secrets (accessToken / PAT / api_key / password) from JSON preview. */
function sanitiseConfigPreview(cfg) {
  if (!cfg) return '—';
  try {
    const obj = typeof cfg === 'string' ? JSON.parse(cfg) : cfg;
    const REDACTED = '***';
    const cleaned = { ...obj };
    ['accessToken', 'pat', 'apiKey', 'api_key', 'password', 'secret'].forEach((k) => {
      if (cleaned[k]) cleaned[k] = REDACTED;
    });
    const s = JSON.stringify(cleaned);
    return s.length > 80 ? s.slice(0, 80) + '…' : s;
  } catch {
    return String(cfg).slice(0, 80);
  }
}

/* ────────────────────────────────────────────────────────────────────
 * Layer-A Discovery rewrite (task #125) — schema-driven CreateRuleModal.
 *
 * The legacy modal hardcoded a per-engine list of rule names and a
 * special-case CODE_REPO form, and fell back to a blank JSON textarea
 * for every other engine — which gave operators no guidance and meant
 * the only way to author a real rule was reading backend javadoc.
 *
 * The new modal fetches the canonical schema from
 * GET /api/v1/discovery/engine-rule-status, which now returns per-rule:
 *   - implementationStatus: REAL | PARTIAL | STUB
 *   - dataSourceSummary:    plain-language sentence
 *   - configFields[]:       {key, type, label, helpText, placeholder,
 *                           defaultValue, required, enumOptions,
 *                           connectorKindFilter, min, max}
 *
 * Each ConfigField type maps to a dedicated input. Operators always
 * see WHAT the rule does today and WHAT it will do once its Layer-B
 * PR lands. The raw-JSON textarea is preserved as an escape hatch
 * behind a "Show raw JSON" toggle so power users can still hand-author
 * fields the schema doesn't model yet.
 * ──────────────────────────────────────────────────────────────────── */

const STATUS_TONE = {
  REAL:    { label: 'Real detection', tone: 'success' },
  PARTIAL: { label: 'Partially real', tone: 'warning' },
  STUB:    { label: 'Stub — synthetic detection', tone: 'warning' },
};

/** Seed a fresh formValues map from a rule's configFields[].defaultValue. */
function seedFormValues(rule) {
  const out = {};
  if (!rule || !rule.configFields) return out;
  for (const f of rule.configFields) {
    if (f.defaultValue !== undefined && f.defaultValue !== null) {
      out[f.key] = f.defaultValue;
    } else if (f.type === 'STRING_LIST') {
      out[f.key] = [];
    } else if (f.type === 'INTEGER') {
      out[f.key] = '';
    } else {
      out[f.key] = '';
    }
  }
  return out;
}

/** Strip empty optional fields from a formValues map before submit. */
function cleanFormValues(rule, values) {
  const out = {};
  if (!rule || !rule.configFields) return out;
  for (const f of rule.configFields) {
    const v = values[f.key];
    const isEmpty =
      v === undefined || v === null || v === '' ||
      (Array.isArray(v) && v.length === 0);
    if (isEmpty) {
      if (f.required) out[f.key] = v; // surface required-but-empty so BE 4xx is clear
      continue;
    }
    if (f.type === 'INTEGER') {
      const n = Number(v);
      out[f.key] = Number.isFinite(n) ? n : v;
    } else {
      out[f.key] = v;
    }
  }
  return out;
}

/** Per-rule renderer: one input per ConfigField. */
function SchemaDrivenRuleForm({ rule, values, onChange, connectors }) {
  if (!rule || !rule.configFields || rule.configFields.length === 0) {
    return (
      <div className="text-[11.5px] text-v2-ink-muted italic">
        This rule has no operator-configurable fields. Saving it as-is is sufficient.
      </div>
    );
  }

  const update = (key, val) => onChange({ ...values, [key]: val });

  return (
    <div className="space-y-3">
      {rule.configFields.map((f) => {
        const v = values[f.key];
        const inputCls = "w-full bg-v2-surface border border-v2-border rounded-v2-sm px-3 py-2 text-[12.5px] outline-none focus:border-v2-action";
        let control;
        switch (f.type) {
          case 'STRING':
            control = (
              <input type="text" value={v || ''} placeholder={f.placeholder || ''}
                onChange={(e) => update(f.key, e.target.value)} className={inputCls} />
            );
            break;
          case 'SECRET':
            control = (
              <input type="password" value={v || ''} placeholder={f.placeholder || ''}
                onChange={(e) => update(f.key, e.target.value)}
                className={inputCls + ' font-mono-v2'} />
            );
            break;
          case 'INTEGER':
            control = (
              <input type="number" value={v ?? ''} placeholder={f.placeholder || ''}
                min={f.min} max={f.max}
                onChange={(e) => update(f.key, e.target.value)}
                className={inputCls + ' w-40'} />
            );
            break;
          case 'ENUM':
            control = (
              <select value={v || ''} onChange={(e) => update(f.key, e.target.value)} className={inputCls}>
                {!f.required && <option value="">(none)</option>}
                {(f.enumOptions || []).map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            );
            break;
          case 'STRING_LIST': {
            const list = Array.isArray(v) ? v : [];
            control = (
              <div className="space-y-1.5">
                {list.map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="text" value={item}
                      onChange={(e) => {
                        const next = list.slice();
                        next[i] = e.target.value;
                        update(f.key, next);
                      }}
                      placeholder={f.placeholder || ''}
                      className={inputCls + ' flex-1'} />
                    <Button variant="ghost" size="sm" onClick={() => {
                      const next = list.slice();
                      next.splice(i, 1);
                      update(f.key, next);
                    }}>Remove</Button>
                  </div>
                ))}
                <Button variant="ghost" size="sm"
                  onClick={() => update(f.key, [...list, ''])}>+ Add</Button>
              </div>
            );
            break;
          }
          case 'CONNECTOR_REF': {
            const filter = f.connectorKindFilter || [];
            const matches = (connectors || []).filter((c) => {
              if (filter.length === 0) return true;
              const kind = (c.kind || c.type || c.connectorKind || '').toLowerCase();
              return filter.some((k) => k.toLowerCase() === kind);
            });
            control = (
              <div>
                <select value={v || ''} onChange={(e) => update(f.key, e.target.value)} className={inputCls}>
                  <option value="">{f.required ? '— pick a connector —' : '(none)'}</option>
                  {matches.map((c) => (
                    <option key={c.id} value={c.id}>{c.name || c.id} ({(c.kind || c.type || '').toLowerCase()})</option>
                  ))}
                </select>
                {matches.length === 0 && filter.length > 0 && (
                  <div className="text-[10.5px] text-v2-warning-strong mt-1">
                    No installed connectors match {filter.join(', ')}. Install one in Connector Center first.
                  </div>
                )}
              </div>
            );
            break;
          }
          default:
            control = (
              <input type="text" value={v || ''} placeholder={f.placeholder || ''}
                onChange={(e) => update(f.key, e.target.value)} className={inputCls} />
            );
        }
        return (
          <div key={f.key}>
            <label className="block text-[10px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
              {f.label}{f.required && <span className="text-v2-danger-strong"> *</span>}
            </label>
            {control}
            {f.helpText && (
              <div className="text-[10.5px] text-v2-ink-muted mt-1 leading-relaxed">{f.helpText}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CreateRuleModal({ onClose, onCreated, editRule = null }) {
  const isEdit = !!editRule;
  const { addToast } = useToast();
  const [catalog, setCatalog] = useState(null);             // engines[] from BE
  const [catalogError, setCatalogError] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [engineType, setEngineType] = useState('');
  const [ruleName, setRuleName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [formValues, setFormValues] = useState({});
  const [showRawJson, setShowRawJson] = useState(false);
  const [rawJson, setRawJson] = useState('{}');
  const [saving, setSaving] = useState(false);

  // Fetch the canonical schema + installed connectors on mount.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.shadowDiscoveryEngineRuleStatus();
        const data = res?.data || res;
        const engines = data?.engines || [];
        setCatalog(engines);
        if (editRule) {
          // Edit mode — identity (engine + rule) is fixed; prefill the saved config.
          setEngineType(editRule.engineType);
          setRuleName(editRule.ruleName);
          setEnabled(editRule.enabled !== false);
          const eng = engines.find((e) => e.engineType === editRule.engineType);
          const rl = (eng?.supportedRules || []).find((r) => r.name === editRule.ruleName);
          const seeded = seedFormValues(rl);
          let saved = editRule.ruleConfig;
          if (typeof saved === 'string') { try { saved = JSON.parse(saved); } catch { saved = {}; } }
          const merged = { ...seeded, ...(saved && typeof saved === 'object' ? saved : {}) };
          setFormValues(merged);
          setRawJson(JSON.stringify(merged, null, 2));
        } else if (engines.length > 0) {
          const firstEngine = engines[0];
          setEngineType(firstEngine.engineType);
          const firstRule = (firstEngine.supportedRules || [])[0];
          if (firstRule) {
            setRuleName(firstRule.name);
            const seeded = seedFormValues(firstRule);
            setFormValues(seeded);
            setRawJson(JSON.stringify(seeded, null, 2));
          }
        }
      } catch (e) {
        setCatalogError(e?.message || 'Failed to load engine catalog');
      }
    })();
    (async () => {
      try {
        const res = await api.getConnectors();
        // api.getConnectors always returns an unwrapped array on res.data.
        setConnectors(Array.isArray(res?.data) ? res.data : []);
      } catch {
        // Connector picker degrades to "no installed connectors match" — not fatal.
      }
    })();
  }, []);

  const currentEngine = useMemo(
    () => (catalog || []).find((e) => e.engineType === engineType),
    [catalog, engineType]);
  const currentRule = useMemo(
    () => (currentEngine?.supportedRules || []).find((r) => r.name === ruleName),
    [currentEngine, ruleName]);

  const onEngineChange = (nextEngine) => {
    setEngineType(nextEngine);
    const eng = (catalog || []).find((e) => e.engineType === nextEngine);
    const first = (eng?.supportedRules || [])[0];
    if (first) {
      setRuleName(first.name);
      const seeded = seedFormValues(first);
      setFormValues(seeded);
      setRawJson(JSON.stringify(seeded, null, 2));
    } else {
      setRuleName('');
      setFormValues({});
      setRawJson('{}');
    }
  };

  const onRuleChange = (nextRule) => {
    setRuleName(nextRule);
    const rule = (currentEngine?.supportedRules || []).find((r) => r.name === nextRule);
    const seeded = seedFormValues(rule);
    setFormValues(seeded);
    setRawJson(JSON.stringify(seeded, null, 2));
  };

  // Keep the two editor views in lockstep when toggling.
  const onToggleRawJson = () => {
    if (showRawJson) {
      // Coming back from raw JSON — parse and re-seed formValues from it.
      try {
        const parsed = JSON.parse(rawJson);
        if (parsed && typeof parsed === 'object') setFormValues(parsed);
        setShowRawJson(false);
      } catch (e) {
        addToast({ tone: 'warning', title: 'Raw JSON invalid', description: e.message });
      }
    } else {
      // Going into raw JSON — serialize the current formValues.
      setRawJson(JSON.stringify(cleanFormValues(currentRule, formValues), null, 2));
      setShowRawJson(true);
    }
  };

  const handleSave = async () => {
    let ruleConfig;
    if (showRawJson) {
      try { ruleConfig = JSON.parse(rawJson); }
      catch (e) {
        addToast({ tone: 'warning', title: 'Config must be valid JSON', description: e.message });
        return;
      }
    } else {
      ruleConfig = cleanFormValues(currentRule, formValues);
      // Pre-flight: enforce required fields before the BE 4xx.
      const missing = (currentRule?.configFields || [])
        .filter((f) => f.required)
        .filter((f) => {
          const v = ruleConfig[f.key];
          return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
        });
      if (missing.length > 0) {
        addToast({
          tone: 'warning',
          title: 'Required fields missing',
          description: missing.map((m) => m.label).join(', '),
        });
        return;
      }
    }
    setSaving(true);
    try {
      /* Body field names mirror DiscoveryRule entity columns:
       *   ruleName, ruleDescription, engineType, ruleConfig, enabled.
       * Backend DTO (DiscoveryRuleService.CreateRuleRequest) uses the
       * same names per DEF-DISCOVERY-RULE-DTO-NAMING-001, so FE/BE/DB
       * vocabularies all stay aligned. */
      if (isEdit) {
        await api.shadowDiscoveryUpdateRule(editRule.id, {
          ruleName: editRule.ruleName,
          ruleDescription: currentRule?.description || editRule.ruleDescription || `${engineType} discovery rule`,
          ruleConfig,
          enabled,
        });
        addToast({ tone: 'success', title: `Rule "${editRule.ruleName}" updated` });
      } else {
        await api.shadowDiscoveryCreateRule({
          engineType,
          ruleName: (ruleName || '').trim(),
          ruleDescription: currentRule?.description || `${engineType} discovery rule`,
          ruleConfig,
          enabled,
        });
        addToast({ tone: 'success', title: `Rule "${ruleName}" created` });
      }
      onCreated();
    } catch (e) {
      addToast({ tone: 'danger', title: isEdit ? 'Update failed' : 'Create failed', description: e?.message });
    } finally { setSaving(false); }
  };

  const statusMeta = STATUS_TONE[currentRule?.implementationStatus] || STATUS_TONE.STUB;

  return (
    <Slideout
      isOpen={true}
      onClose={onClose}
      ariaLabel={isEdit ? 'Edit discovery rule' : 'New discovery rule'}
      width={520}
      header={
        <div>
          <h2 className="text-[15px] font-bold tracking-tight m-0">{isEdit ? 'Edit discovery rule' : 'New discovery rule'}</h2>
          <p className="text-[12px] text-v2-ink-secondary m-0 mt-0.5 leading-relaxed">
            {isEdit
              ? 'Update this rule’s configuration. The engine and rule name stay the same.'
              : 'Pick an engine and a rule; the form below shows you exactly what the rule needs.'}
          </p>
        </div>
      }
    >
      <div className="space-y-4 text-[12.5px]">
        {catalogError && (
          <div className="bg-v2-surface border border-v2-danger rounded-v2-sm p-3 text-[11.5px] text-v2-danger-strong">
            Couldn't load engine catalog: {catalogError}
          </div>
        )}
        {!catalog && !catalogError && (
          <div className="space-y-2">
            <Skeleton width="100%" height={32} />
            <Skeleton width="100%" height={120} />
          </div>
        )}
        {catalog && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
                  Engine
                </label>
                <select value={engineType} onChange={(e) => onEngineChange(e.target.value)} disabled={isEdit}
                  className="w-full bg-v2-surface border border-v2-border rounded-v2-sm px-3 py-2 text-[12.5px] outline-none focus:border-v2-action disabled:opacity-60 disabled:cursor-not-allowed">
                  {catalog.map((e) => (
                    <option key={e.engineType} value={e.engineType}>{e.label} · {e.engineType}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider font-semibold text-v2-ink-muted mb-1">
                  Rule name
                </label>
                <select value={ruleName} onChange={(e) => onRuleChange(e.target.value)} disabled={isEdit}
                  className="w-full bg-v2-surface border border-v2-border rounded-v2-sm px-3 py-2 text-[12.5px] outline-none focus:border-v2-action disabled:opacity-60 disabled:cursor-not-allowed">
                  {(currentEngine?.supportedRules || []).map((r) => (
                    <option key={r.name} value={r.name}>{r.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {currentRule && (
              <div className="bg-v2-muted rounded-v2-sm p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <Pill tone={statusMeta.tone} size="sm" dot>{statusMeta.label}</Pill>
                  <div className="flex-1 text-[11.5px] text-v2-ink leading-relaxed">
                    {currentRule.description}
                  </div>
                </div>
                {currentRule.dataSourceSummary && (
                  <div className="text-[11px] text-v2-ink-muted leading-relaxed border-t border-v2-border-subtle pt-2">
                    <span className="font-semibold text-v2-ink-secondary">What this rule does today:</span>{' '}
                    {currentRule.dataSourceSummary}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">
                {showRawJson ? 'Raw configuration (JSON)' : 'Rule configuration'}
              </div>
              <Button variant="ghost" size="sm" onClick={onToggleRawJson}>
                {showRawJson ? 'Use form' : 'Show raw JSON'}
              </Button>
            </div>

            {showRawJson ? (
              <div>
                <textarea value={rawJson} onChange={(e) => setRawJson(e.target.value)}
                  rows={10} spellCheck="false"
                  className="w-full bg-v2-muted border border-v2-border rounded-v2-sm px-3 py-2 text-[11.5px] font-mono-v2 outline-none focus:border-v2-action" />
                <div className="text-[10.5px] text-v2-ink-muted mt-1">
                  Escape hatch for hand-authored fields the schema doesn't model yet.
                </div>
              </div>
            ) : (
              <SchemaDrivenRuleForm
                rule={currentRule}
                values={formValues}
                onChange={setFormValues}
                connectors={connectors}
              />
            )}

            <label className="flex items-center gap-2 text-[12.5px]">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Enable rule immediately
            </label>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-v2-border-subtle">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button variant="primary" onClick={handleSave} loading={saving} disabled={!currentRule}>
                {isEdit ? 'Save changes' : 'Save rule'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Slideout>
  );
}
